import { HttpResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, Observable } from 'rxjs';
import { catchError, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { BackgroundJobNotificationService } from 'Src/ng2/shared/services/background-job-notification-service/background-job-notification-service';
import { BackgroundJob } from 'Src/ng2/shared/services/background-job/background-job.service';
import { LoadDocLogs } from '../actions/doc-logs-actions';
import { UpdatePatchProcessing } from '../actions/patch-processing-actions';
import * as singleStudentActions from '../actions/single-student-actions';
import * as studentActions from '../actions/students-actions';
import {
  BulkUpdateAcademicStudentsSuccess,
  BulkUpdateAttendanceStudentsSuccess,
  BulkUpdateMyStudentsSuccess,
  BulkUpdateStudentsSuccess,
} from '../actions/students-actions';
import * as calculatedFieldsActions from '../actions/students-actions/calculated-fields-action';
import { PatchUtilities } from '../utilities/patch-utilities';
import { DIM1_MADLIB_PATHS } from './../../../app/routes/main/route-resolvers/mad-lib-options-resolver';
import { ApiService } from './../../shared/services/api-service/api-service';
import { ISnackBarData, SnackBarService } from './../../shared/services/snackbar/snackbar.service';
import { LoadMadlibOptions } from './../actions/madlib-options-actions';
import { ATTENDANCE_PROJECTION, generateQuery, POSTSECONDARY_PROJECTION } from './projections';
import { ACADEMIC_JOINS, ACADEMIC_PROJECTION } from './projections/academic';
import { ATTENDANCE_JOINS } from './projections/attendance';
import { MY_STUDENTS_JOINS, MY_STUDENTS_PROJECTION } from './projections/my-students';

@Injectable()
export class StudentEffects {
  constructor(
    private actions$: Actions,
    private apiService: ApiService,
    private store$: Store<any>,
    private snackBarService: SnackBarService,
    private backgroundJob: BackgroundJob,
    private injector: Injector,
    private backgroundJobNotificationService: BackgroundJobNotificationService,
  ) {}

  snackBarData: ISnackBarData = { message: '', duration: 2000 };

  @Effect({ dispatch: false })
  /* istanbul ignore next */
  updateStudents$ = this.actions$.pipe(
    ofType<any>(studentActions.BULK_UPDATE_STUDENTS),
    withLatestFrom(this.store$),
    switchMap(([action, storeState]) => {
      const backgroundJobSpinner = this.injector.get('BackgroundJobSpinner');
      const sdcActionsSvc = this.injector.get('sdcActions');
      const { patches } = action.payload;
      const httpResponseObsv$: Observable<HttpResponse<any>> = this.apiService.patchStudents(patches);
      return httpResponseObsv$.pipe(
        take(1),
        tap(response => {
          const jobId = response.headers.get('nv-background-jobs');
          const promise = (this.backgroundJob as any)(jobId).promise();
          if (jobId) {
            backgroundJobSpinner.activateSpinner('Updating students...', promise, null);
            promise.then(() => {
              // just updates sdc store. Passes 'ngrx' so method knows to bypass EventBus
              sdcActionsSvc.recordMultiStudentUpdate([patches], 'ngrx');

              this.snackBarService.showBatchActionToast({ success: true });

              // dispatch action to update madlib-options in store (JE)
              const schoolId = storeState.PORTAL_STORE.school.school._id;
              const payload = { paths: DIM1_MADLIB_PATHS, schoolId };
              this.backgroundJobNotificationService.sendMessage({ backgroundJob: 'BulkStudentUpdate' });
              // reload madlibs
              // TODO: remove after list refactor
              const newPatches = PatchUtilities.shapePatchesForStore([patches]);
              const actions = [
                new LoadMadlibOptions(payload),
                new BulkUpdateStudentsSuccess(newPatches),
                new BulkUpdateMyStudentsSuccess(newPatches),
                new BulkUpdateAttendanceStudentsSuccess(newPatches), // TODO: remove after list refactor
                new BulkUpdateAcademicStudentsSuccess(newPatches), // TODO: remove after list refactor
              ];
              actions.forEach(action => this.store$.dispatch(action));
            });
          }
        }),
        catchError(error => {
          /**
           * ordinarily fail case would simply return the slice as is.  But there is
           * no slice/reducer set up to manage ALL students. We're just using this action
           * for its side effect. So just returning error silently for now - JYR
           */
          this.snackBarService.showBatchActionToast({ success: false });
          return error;
        }),
      );
    }),
  );

  @Effect()
  loadMyStudents$ = this.actions$.pipe(
    ofType(studentActions.LOAD_MY_STUDENTS),
    switchMap((action: any) => {
      const { schoolId } = action.payload;
      const projection = MY_STUDENTS_PROJECTION;
      const joins = MY_STUDENTS_JOINS;
      const where = { schoolStatus: { $in: ['A'] } };
      const payload = { schoolId, projection, joins, where };

      return this.apiService.getStudents(payload).pipe(
        mergeMap(students => [
          new studentActions.LoadMyStudentsSuccess(students),
          new studentActions.UpdateMyStudentsProjectionSuccess(payload.projection),
        ]),
        catchError(error =>
          from([
            new studentActions.LoadMyStudentsFail(error),
            new studentActions.UpdateMyStudentsProjectionFail(error),
          ]),
        ),
      );
    }),
  );

  @Effect()
  loadSingleStudent$ = this.actions$.pipe(
    ofType(singleStudentActions.LOAD_STUDENT),
    switchMap((action: any) => {
      const { studentId } = action.payload;
      return this.apiService.getSingleStudent(studentId).pipe(
        mergeMap(students => [new singleStudentActions.LoadSingleStudentSuccess(students)]),
        catchError(error => from([new singleStudentActions.LoadSinglesStudentFail(error)])),
      );
    }),
  );

  @Effect()
  loadSingleStudentFromOtherSchool$ = this.actions$.pipe(
    ofType(singleStudentActions.LOAD_STUDENT_FROM_OTHER_SCHOOL),
    switchMap((action: any) => {
      const { studentId, otherSchoolType = 'summer' } = action.payload;
      return this.apiService.getSingleStudentFromOtherSchool(studentId, otherSchoolType).pipe(
        mergeMap(students => [new singleStudentActions.LoadSingleStudentFromOtherSchoolSuccess(students)]),
        catchError(error => from([new singleStudentActions.LoadSinglesStudentFromOtherSchoolFail(error)])),
      );
    }),
  );

  @Effect()
  updateSingleStudent$ = this.actions$.pipe(
    ofType(singleStudentActions.UPDATE_STUDENT),
    switchMap((action: { type: string; payload: any }) => {
      const { payload } = action;
      return this.apiService.patchSingleStudent(payload).pipe(
        mergeMap(students => {
          const { _id, schoolId } = students;
          const docLogFilter = { studentId: _id, schoolId };
          return [
            new singleStudentActions.UpdateSingleStudentSuccess(students),
            new LoadDocLogs(docLogFilter),
            new UpdatePatchProcessing(),
          ];
        }),
        catchError(error => {
          return from([new singleStudentActions.UpdateSingleStudentFail(error), new UpdatePatchProcessing()]);
        }),
      );
    }),
  );

  @Effect()
  loadAttendanceStudents$ = this.actions$.pipe(
    ofType(studentActions.LOAD_ATTENDANCE_STUDENTS),
    switchMap((action: any) => {
      const { schoolId } = action.payload;
      const projection = ATTENDANCE_PROJECTION;
      const joins = ATTENDANCE_JOINS;
      const where = { schoolStatus: { $in: ['A'] } };
      const payload = { schoolId, projection, joins, where };

      return this.apiService.getStudents(payload).pipe(
        mergeMap(students => [
          new studentActions.LoadAttendanceStudentsSuccess(students),
          new studentActions.UpdateAttendanceStudentsProjectionSuccess(payload.projection),
        ]),
        catchError(error =>
          from([
            new studentActions.LoadAttendanceStudentsFail(error),
            new studentActions.UpdateAttendanceStudentsProjectionFail(error),
          ]),
        ),
      );
    }),
  );

  @Effect()
  loadAcademicStudents$ = this.actions$.pipe(
    ofType(studentActions.LOAD_ACADEMIC_STUDENTS),
    switchMap((action: any) => {
      const { schoolId } = action.payload;
      const projection = ACADEMIC_PROJECTION;
      const joins = ACADEMIC_JOINS;
      const where = { schoolStatus: { $in: ['A'] } };
      const payload = { schoolId, projection, joins, where };

      return this.apiService.getStudents(payload).pipe(
        mergeMap(students => [
          new studentActions.LoadAcademicStudentsSuccess(students),
          new studentActions.UpdateAcademicStudentsProjectionSuccess(payload.projection),
        ]),
        catchError(error =>
          from([
            new studentActions.LoadAcademicStudentsFail(error),
            new studentActions.UpdateAcademicStudentsProjectionFail(error),
          ]),
        ),
      );
    }),
  );

  @Effect()
  loadPostsecondaryStudents$ = this.actions$.pipe(
    ofType(studentActions.LOAD_POSTSECONDARY_STUDENTS),
    switchMap((action: any) => {
      const { schoolId } = action.payload;
      const projection = POSTSECONDARY_PROJECTION;
      const joins = [];
      const where = {
        schoolStatus: { $in: ['A'] },
        isHS: true,
      };
      const payload = { schoolId, projection, joins, where };
      return this.apiService.getStudents(payload).pipe(
        mergeMap(students => [
          new studentActions.LoadPostsecondaryStudentsSuccess(students),
          new studentActions.UpdatePostsecondaryStudentsProjectionSuccess(payload.projection),
        ]),
        catchError(error =>
          from([
            new studentActions.LoadPostsecondaryStudentsFail(error),
            new studentActions.UpdatePostsecondaryStudentsProjectionFail(error),
          ]),
        ),
      );
    }),
  );

  @Effect()
  loadPostsecondaryCalculatedFields$ = this.actions$.pipe(
    ofType<any>(calculatedFieldsActions.LOAD_POSTSECONDARY_CALCUALTED_FIELDS),
    switchMap(({ payload }) => {
      const { schoolId } = payload;
      // tslint:disable-next-line
      const where = '{\\"schoolStatus\\": \\"A\\", \\"isHS\\": true}';
      const query = generateQuery(schoolId, where);
      const queryPayload = {
        query,
        fetchPolicy: 'no-cache',
      };
      return this.apiService.getStudentsGraphQL(queryPayload).pipe(
        mergeMap(students => [new calculatedFieldsActions.LoadPostsecondaryCalculatedFieldsSuccess(students)]),
        catchError(error => from([new calculatedFieldsActions.LoadPostsecondaryCalculatedFieldsFail(error)])),
      );
    }),
  );
}
